import type { CSSObject } from '@emotion/react'
import {
  Color,
  FontWeight,
  OppositeTheme,
  Spacing,
  Typography,
} from '@opallabs/gem'
import type { StyleProps } from './types'

const sides: CSSObject = {
  alignItems: 'center',
  display: 'flex',
  flex: '1 15%',
}

const middle: CSSObject = {
  display: 'flex',
  flex: '2 70%',
}

export const container = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N000,
  color: Color[theme].Text.Default,
  display: 'flex',
  flexDirection: 'column',
  paddingBlockStart: Spacing.XS * 7,
  paddingInline: Spacing.XS * 7,
})

export const metadataLine: CSSObject = {
  alignContent: 'center',
  display: 'flex',
  justifyContent: 'space-between',
}

export const titleAndSearch = ({
  hasParent,
}: {
  hasParent: boolean
}): CSSObject => ({
  ...middle,
  alignItems: 'center',
  justifyContent: hasParent ? 'center' : 'flex-start',
})

export const searchContainer: CSSObject = {
  inlineSize: 300,
}

export const title = ({
  hasTitle,
  theme,
}: StyleProps & {
  hasTitle: boolean
}): CSSObject => ({
  color: hasTitle ? Color[theme].Text.Default : Color[theme].Text.Gray,
  ...Typography.H500,
  fontWeight: FontWeight.Bold,
})

export const parent: CSSObject = {
  ...sides,
  marginInlineEnd: Spacing.S,
}

export const avatar: CSSObject = {
  ...sides,
  marginInlineStart: Spacing.S,
  justifyContent: 'flex-end',
}

export const search: CSSObject = {
  marginInlineStart: Spacing.XS * 3,
}

export const children = ({
  hasActions,
  hasControls,
}: {
  hasActions: boolean
  hasControls: boolean
}): CSSObject => ({
  alignContent: 'center',
  blockSize: Spacing.XS * 8 + Spacing.XS * 2,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent:
    hasActions && hasControls
      ? 'space-between'
      : hasActions
        ? 'flex-end'
        : 'flex-start',
  marginBlockEnd: Spacing.XS * 7 + 1,
})

export const actions: CSSObject = {
  ...sides,
  justifyContent: 'flex-end',
  marginBlockStart: Spacing.XS * 2,
  marginInlineStart: Spacing.S,
}

export const action: CSSObject = {
  marginInlineStart: Spacing.S * 1.5,
}

export const controls: CSSObject = {
  ...sides,
  marginBlockStart: Spacing.XS * 3,
  marginInlineEnd: Spacing.S,
}

export const controlSet = ({ theme }: StyleProps): CSSObject => ({
  alignItems: 'center',
  display: 'flex',

  '& + &': {
    borderColor: Color[theme].Surface.N300,
    borderWidth: 0,
    borderInlineStartWidth: 1,
    borderStyle: 'solid',
    marginInlineStart: Spacing.S,
    paddingInlineStart: Spacing.S,

    '@media (prefers-contrast: more)': {
      borderColor: Color[OppositeTheme[theme]].Surface.N000,
    },
  },
})

export const control = ({ theme }: StyleProps): CSSObject => ({
  color: Color[theme].Surface.N999,
  '& + &': {
    marginInlineStart: Spacing.XS,
  },

  '@media (prefers-contrast: more)': {
    color: Color[OppositeTheme[theme]].Surface.N000,
  },
})
